import React, { useEffect, useRef, useState } from 'react';
import Page from '../../../layout/Page';
import Row from '../../../layout/Row';
import Col from '../../../layout/Col';
import { QuestRoute } from '../../../api/QuestRoutes';
import Checkbox from '../../../forms/Checkbox';
import { beginningOfMonth, exportToPDF, generateId, inputDate, shortHandDate, toDHMS } from '../../../utility/functions';
import Collapsible from '../../../layout/Collapsable';
import HamQuest from '../../../api/HamQuestGiver';
import Form from '../../../forms/Form';
import Button from '../../../forms/Button';
import DateSelector from '../../../forms/DateRange';
import styled from 'styled-components';
import Card from '../../../forms/Card';
import CompanyLogo from '../../../CompanyLogo';
import Heading from '../../../forms/Heading';
import Table from '../../../table/Table';
import LocModelDropdown from '../../../LocModelDropdown';
import Select from '../../../forms/Select';
import { HSOptions } from '../../../../constants/HSOptions';
import { SCREEN_WIDTH } from '../../../../constants/config';

const HeaderRow = styled.div`
    display: flex;
    gap: 4em;
    justify-content: space-between;
    align-items: flex-start;
`
const ReportSegment = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
    margin-bottom: 0.5em;
    padding-bottom: 0.2em;
`

const grouper = {
    'Hour': 24,
    'Day': 30,
    'Week': 52,
    'Month': 12,
};

const groupLabel = {
    'Hour': 'Date',
    'Day': 'Date',
    'Week': 'Week Of',
    'Month': 'Month',
    'Year': 'Year',
};

const groupingLabel = {
    'Hour': (hour) => `${hour < 12 ? 'AM' : 'PM'}: ${hour % 12 || 12}:00 - ${hour % 12 || 12}:59`,
    'Day': () => null,
    'Week': (week) => week,
    'Month': (month) => month,
    'Year': (year) => year,
};

const TabButton = styled(Button)`
    white-space: nowrap;

    background: ${({ active, theme }) => active ? theme.button.active : ''};
    color: ${({ active, theme }) => active ? theme.button.activeText : ''};

    &:hover {
        background: ${({ active, theme }) => active ? theme.button.active : ''};
        color: ${({ active, theme }) => active ? theme.button.activeText : ''};
    }
`;


const Usage = ({ ...props }) => {

    const cardRef = useRef();
    const [tabIndex, setTabIndex] = useState(0);

    const [items, setItems] = useState(null);
    const [filteredItems, setFilteredItems] = useState([]);
    const [locModels, setLocModels] = useState(null);
    const [usageData, setUsageData] = useState(null);
    const [usageConfig, setUsageConfig] = useState({
        locations: [],
        grouping: null
    });

    const tabs = ['Kiosk Items', 'Buy Ups', 'Timers', 'Passes'];

    useEffect(() => {
        HamQuest.get(QuestRoute.items.passes)
            .then(res => {
                setItems({
                    'Kiosk Items': [
                        { value: 1, label: '1' },
                        { value: 2, label: '2' },
                        { value: 3, label: '3' },
                        { value: 4, label: '4' },
                        { value: 5, label: '5' },
                        { value: 6, label: '6' },
                        { value: 7, label: '7' },
                        { value: 8, label: '8' }
                    ],
                    'Buy Ups': [
                        { value: 100, label: '100' },
                        { value: 101, label: '101' },
                        { value: 102, label: '102' },
                        { value: 103, label: '103' },
                        { value: 104, label: '104' },
                        { value: 105, label: '105' },
                        { value: 106, label: '106' },
                        { value: 107, label: '107' },
                        { value: 108, label: '108' },
                        { value: 109, label: '109' },
                        { value: 110, label: '110' },
                        { value: 111, label: '111' },
                        { value: 112, label: '112' },
                        { value: 113, label: '113' },
                        { value: 114, label: '114' },
                        { value: 115, label: '115' }
                    ],
                    'Passes': res
                })
            })
    }, [])

    const TableDetails = ({ data, grouping, index }) => {

        let headers = [
            {
                label: groupLabel[grouping],
                propKey: 'date',
                type: 'date'
            },
        ]
        if (grouping === 'Hour') {
            headers.push({
                label: grouping,
                propKey: 'grouping',
            })
        }
        if (index !== 3) {
            filteredItems.sort().forEach(e => {
                headers.push({
                    label: e,
                    propKey: e,
                })
            })
        } else {
            filteredItems.sort().forEach(e => {
                headers.push({
                    label: items.Passes.find(f => f.value === e).label,
                    propKey: items.Passes.find(f => f.value === e).value,
                })
            })
        }

        headers.push({
            label: 'Total',
            propKey: 'total',
        })

        if (index === 2) {
            headers = [
                {
                    label: groupLabel[grouping],
                    propKey: 'date',
                    type: 'date'
                },
            ]
            if (grouping === 'Hour') {
                headers.push({
                    label: grouping,
                    propKey: 'grouping',
                })
            }
            headers.push({
                label: 'Activations',
                propKey: 'activations',
            })
            headers.push({
                label: 'Time Used',
                propKey: 'timeUsed',
            })
            headers.push({
                label: 'Average Time Used',
                propKey: 'averageTimeUsed',
            })
        }

        return (
            <Table
                config={{
                    disableExport: true,
                    disableHeaderSearch: true,
                    disablePagerResults: true,
                    disableSlideAnimation: true,
                    hideNoResults: true,
                    data: data,
                    headers: headers,
                    resultsPerPage: grouper[grouping],
                    renderFormat: (e) => {
                        if (index === 2) {
                            return (
                                <tr key={generateId()}>
                                    <td>{grouping === 'Month' ? `${new Date(e.date).getMonth() + 1}/${new Date(e.date).getFullYear()}` : shortHandDate(e.date)}</td>
                                    {grouping === 'Hour' && <td>{groupingLabel[grouping](e.grouping)}</td>}
                                    <td>{e.activations}</td>
                                    <td>{toDHMS(e.timeUsed)}</td>
                                    <td>{toDHMS(e.averageTimeUsed)}</td>
                                </tr>
                            )
                        }

                        if (index === 3) {
                            return (
                                <tr key={generateId()}>
                                    <td>{grouping === 'Month' ? `${new Date(e.date).getMonth() + 1}/${new Date(e.date).getFullYear()}` : shortHandDate(e.date)}</td>
                                    {grouping === 'Hour' && <td>{groupingLabel[grouping](e.grouping)}</td>}
                                    {filteredItems.includes('DailyPass') && <td>{e.dailyPass}</td>}
                                    {filteredItems.includes('FleetPass') && <td>{e.fleetPass}</td>}
                                    {filteredItems.includes('PrepaidPass') && <td>{e.prepaidPass}</td>}
                                    <td>{
                                        (filteredItems.includes('DailyPass') ? e.dailyPass : 0) +
                                        (filteredItems.includes('FleetPass') ? e.fleetPass : 0) +
                                        (filteredItems.includes('PrepaidPass') ? e.prepaidPass : 0)
                                    }</td>
                                </tr>
                            )
                        }

                        const items = e.items;
                        return (
                            <tr key={generateId()}>
                                <td>{grouping === 'Month' ? `${new Date(e.date).getMonth() + 1}/${new Date(e.date).getFullYear()}` : shortHandDate(e.date)}</td>
                                {grouping === 'Hour' && <td>{groupingLabel[grouping](e.grouping)}</td>}
                                {items.filter(g => filteredItems.includes(g.item)).map(f => <td>{f.count}</td>)}
                                <td>{
                                    filteredItems.length
                                        ? items.filter(g => filteredItems.find(h => h === g.item)).reduce((a, b) => a + b.count, 0)
                                        : e.total
                                }</td>
                            </tr>
                        )
                    },
                    finalRow: (data, total) => {
                        // console.log(data)
                        if (index === 2) {
                            return (
                                <>
                                    <tr key={generateId()}>
                                        <td><b>Section Total</b></td>
                                        {grouping === 'Hour' && <td><b>-</b></td>}
                                        <td>{data.reduce((a, b) => a + b.activations, 0)}</td>
                                        <td>{toDHMS(data.reduce((a, b) => a + b.timeUsed, 0))}</td>
                                        <td>{toDHMS(data.reduce((a, b) => a + b.averageTimeUsed, 0))}</td>
                                    </tr>
                                    <tr key={generateId()}>
                                        <td><b>Total</b></td>
                                        {grouping === 'Hour' && <td><b>-</b></td>}
                                        <td>{total.reduce((a, b) => a + b.activations, 0)}</td>
                                        <td>{toDHMS(total.reduce((a, b) => a + b.timeUsed, 0))}</td>
                                        <td>{toDHMS(total.reduce((a, b) => a + b.averageTimeUsed, 0))}</td>
                                    </tr>
                                </>

                            )
                        }
                        if (index === 3) {
                            return (
                                <>
                                    <tr key={generateId()}>
                                        <td><b>Section Total</b></td>
                                        {grouping === 'Hour' && <td><b>-</b></td>}
                                        {filteredItems.includes('DailyPass') && <td>{data.reduce((a, b) => a + b.dailyPass, 0)}</td>}
                                        {filteredItems.includes('FleetPass') && <td>{data.reduce((a, b) => a + b.fleetPass, 0)}</td>}
                                        {filteredItems.includes('PrepaidPass') && <td>{data.reduce((a, b) => a + b.prepaidPass, 0)}</td>}
                                        <td>{
                                            (filteredItems.includes('DailyPass') ? data.reduce((a, b) => a + b.dailyPass, 0) : 0) +
                                            (filteredItems.includes('FleetPass') ? data.reduce((a, b) => a + b.fleetPass, 0) : 0) +
                                            (filteredItems.includes('PrepaidPass') ? data.reduce((a, b) => a + b.prepaidPass, 0) : 0)
                                        }</td>
                                    </tr>
                                    <tr key={generateId()}>
                                        <td><b>Total</b></td>
                                        {grouping === 'Hour' && <td><b>-</b></td>}
                                        {filteredItems.includes('DailyPass') && <td>{total.reduce((a, b) => a + b.dailyPass, 0)}</td>}
                                        {filteredItems.includes('FleetPass') && <td>{total.reduce((a, b) => a + b.fleetPass, 0)}</td>}
                                        {filteredItems.includes('PrepaidPass') && <td>{total.reduce((a, b) => a + b.prepaidPass, 0)}</td>}
                                        <td>{
                                            (filteredItems.includes('DailyPass') ? total.reduce((a, b) => a + b.dailyPass, 0) : 0) +
                                            (filteredItems.includes('FleetPass') ? total.reduce((a, b) => a + b.fleetPass, 0) : 0) +
                                            (filteredItems.includes('PrepaidPass') ? total.reduce((a, b) => a + b.prepaidPass, 0) : 0)
                                        }</td>
                                    </tr>
                                </>

                            )
                        }

                        let items = { total: 0 };
                        data.forEach(e => {
                            e.items.forEach(f => {
                                items[f.item] = (items[f.item] || 0) + f.count;
                                items.total += f.count;
                            })
                        })
                        let totalItems = { total: 0 };
                        total.forEach(e => {
                            e.items.forEach(f => {
                                totalItems[f.item] = (totalItems[f.item] || 0) + f.count;
                                totalItems.total += f.count;
                            })
                        })

                        return (
                            <>
                                <tr key={generateId()}>
                                    <td><b>Section Total</b></td>
                                    {grouping === 'Hour' && <td><b>-</b></td>}
                                    {
                                        filteredItems.sort().map(e => <td>{items[e]}</td>)
                                    }
                                    <td>{filteredItems.length ? filteredItems.map(e => items[e]).reduce((a, b) => a + b, 0) : items.total}</td>
                                </tr>
                                <tr key={generateId()}>
                                    <td><b>Total</b></td>
                                    {grouping === 'Hour' && <td><b>-</b></td>}
                                    {
                                        filteredItems.sort().map(e => <td>{totalItems[e]}</td>)
                                    }
                                    <td>{filteredItems.length ? filteredItems.map(e => totalItems[e]).reduce((a, b) => a + b, 0) : totalItems.total}</td>
                                </tr>
                            </>

                        )
                    }
                }}
            />
        );
    }

    return (
        <Page>
            <Row wrap>
                {tabs.map((e, i) => (
                    <TabButton
                        key={`${i}-tabBtn`}
                        onClick={() => {
                            setTabIndex(i);
                            setFilteredItems([]);
                        }}
                        active={tabIndex === i}
                        label={e}
                    />
                ))}
            </Row>
            <Row align='flex-start' style={{ width: '100%' }} wrap>
                <Col style={{ width: '30%' }}>
                    <Button
                        disabled={!usageData}
                        onClick={() => exportToPDF(cardRef, `Usage Report`)}
                    >Export</Button>
                    <Form
                        inputs={{
                            items: [],
                            groupBy: 'Hour',
                            startDate: beginningOfMonth,
                            endDate: inputDate(),
                        }}
                        onSubmit={(e) => {
                            const { startDate, endDate, groupBy } = e;
                            setUsageConfig({
                                locations: locModels.locations,
                                grouping: groupBy,
                            });
                            return HamQuest.post(QuestRoute.reports.usage, {
                                startDate,
                                endDate,
                                groupBy,
                                query: locModels.query,
                            })
                                .then(res => {
                                    setUsageConfig((e) => ({
                                        ...e,
                                        startDate: res.startDate,
                                        endDate: res.endDate,
                                    }));

                                    setUsageData([
                                        res.kiosks,
                                        res.buyUps,
                                        res.timers,
                                        res.passes,
                                    ])
                                })
                        }}
                        text='Filter'
                    >
                        {({ values }) => (
                            <>
                                <Select
                                    name='groupBy'
                                    label='Grouping'
                                    options={HSOptions.reports.grouping}
                                    required
                                />
                                <DateSelector />
                                <LocModelDropdown
                                    startDate={values.startDate}
                                    endDate={values.endDate}
                                    getState={setLocModels}
                                    tabIndex={tabIndex}
                                />

                                {items && items[tabs[tabIndex]] && (
                                    <Collapsible label={'Items'}>
                                        <Button
                                            onClick={() => (
                                                setFilteredItems(
                                                    filteredItems.length === items[tabs[tabIndex]].length
                                                        ? []
                                                        : items[tabs[tabIndex]].map(e => e.value)
                                                ))
                                            }
                                            label={filteredItems.length === items[tabs[tabIndex]].length ? 'Clear' : 'All'}
                                        />
                                        {
                                            items[tabs[tabIndex]].map((e, i) => (
                                                <Checkbox
                                                    key={`${i}-items`}
                                                    label={e.label}
                                                    name={'items'}
                                                    value={e.value}
                                                    checked={filteredItems.includes(e.value)}
                                                    style={{ fontSize: '0.5em', }}
                                                    inputClick={() => {
                                                        setFilteredItems(
                                                            filteredItems.includes(e.value)
                                                                ? filteredItems.filter(f => f !== e.value)
                                                                : [...filteredItems, e.value]
                                                        )
                                                    }}
                                                />
                                            ))
                                        }
                                    </Collapsible>
                                )}
                            </>
                        )}

                    </Form>
                </Col>
                {usageData ?
                    <Card title={`${tabs[tabIndex]} Usage Report`} icon='clipboard' style={{ width: '65%' }}>
                        <div ref={cardRef}>
                            <Col style={SCREEN_WIDTH ? { maxWidth: '75svw' } : { flex: '3' }}>
                                <HeaderRow style={{ marginBottom: '3em' }}>
                                    <Col>
                                        <Heading>Period</Heading>
                                        <HeaderRow>
                                            <span>Start: {shortHandDate(usageConfig.startDate)}</span>
                                        </HeaderRow>
                                        <HeaderRow>
                                            <span>End: {shortHandDate(usageConfig.endDate)}</span>
                                        </HeaderRow>
                                    </Col>
                                    <CompanyLogo dim={6} />
                                </HeaderRow>
                                <ReportSegment>
                                    <Col style={{ gap: '2em' }}>
                                        <HeaderRow>
                                            <Col>
                                                <HeaderRow>
                                                    <b>Locations:</b>
                                                    <Row>{usageConfig.locations.join(', ')}</Row>
                                                </HeaderRow>
                                            </Col>
                                        </HeaderRow>

                                        <TableDetails
                                            // label='Gross Sales by Item'
                                            index={tabIndex}
                                            data={usageData[tabIndex]}
                                            grouping={usageConfig.grouping}
                                        />

                                    </Col>
                                </ReportSegment>
                            </Col>
                        </div>
                    </Card>
                    : null}
            </Row >
        </Page >
    );
}

export default Usage;
